import { Component, OnInit, Input, OnDestroy, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { debounceTime, tap, filter, Subscription, merge, switchMap } from 'rxjs';

import { WebSocketEventsEnum } from '@shared/websocket';
import { Location } from '@shared/location/models';
import { Reply } from '@shared/reply/models';
import {
  IParty,
  SMS_REPLY_PATTERN,
  NOT_ONLY_WHITESPACE_PATTERN,
} from '@tablesready/common-ui';
import { MessageSenderService, PartiesService, ReplyService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { SocketService } from '@tablesready/frontend-store';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-chat-party',
  templateUrl: './chat-party.component.html',
  styleUrls: ['./chat-party.component.scss'],
})
export class ChatPartyComponent implements OnInit, OnDestroy {
  @Input() location: Location;
  @Input() party: IParty | null;
  @Input() showReplyForm = true;
  @Input() showTemplates = false;

  icon = AppIcon;
  form?: UntypedFormGroup;
  smsReply = '';
  filteredMessages = [];
  maxLength: number;
  templates: Reply[] = [];
  replyTemplate: string = null; // Added this to display placeholder on ng-select with [multiple]=false option
  dropDownOpened = false;
  readonly maxAnsiLength: number;
  readonly maxNonAnsiLength: number;
  private debounceMs = 400;
  private subscriptions = new Subscription();

  constructor(
    private partiesService: PartiesService,
    private readonly modalController: FrontendModalService,
    private messageSenderService: MessageSenderService,
    private socketsService: SocketService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
    private readonly replyService: ReplyService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.maxAnsiLength = Number(this.appVariables.WHITELABEL_SMS_CHARS);
    this.maxNonAnsiLength = Number(this.appVariables.WHITELABEL_SMS_NON_ANSI_CHARS);
    this.maxLength = this.maxAnsiLength;
  }

  ngOnInit(): void {
    const eventsSubscription = merge(
      this.socketsService.getEventStream(WebSocketEventsEnum.parties),
      this.socketsService.getEventStream(WebSocketEventsEnum.TextSmsEvent),
      this.socketsService.getEventStream(WebSocketEventsEnum.VoiceSmsEvent),
      this.socketsService.getEventStream(WebSocketEventsEnum.InboundSmsEvent),
    )
      .pipe(
        debounceTime(this.debounceMs),
        tap(() => this.partiesEventHandler()),
      )
      .subscribe();
    this.subscriptions.add(eventsSubscription);

    if (this.showTemplates) {
      this.subscriptions.add(this.replyService.listenToChanges().subscribe());
      const repliesSubscription = this.replyService.load()
        .pipe(
          switchMap(() => this.replyService.selectAll()),
          tap(replies => this.templates = replies),
        )
        .subscribe();
      this.subscriptions.add(repliesSubscription);
    }

    this.form = this.formBuilder.group({
      smsReply: [null, [
        Validators.required,
        Validators.maxLength(this.maxLength),
        Validators.pattern(SMS_REPLY_PATTERN),
        Validators.pattern(NOT_ONLY_WHITESPACE_PATTERN),
      ]],
      replyTemplate: [null],
    });

    const replyTemplateSub = this.form.get('replyTemplate').valueChanges
      .pipe(
        filter(id => !!id),
        tap(id => {
          const template = this.templates.find(template => template.id === id);
          if (template) {
            this.smsReplyControl.setValue(template.text);
          }
        }),
      ).subscribe();
    this.subscriptions.add(replyTemplateSub);

    this.mergeInboundOutbound();
  }

  mergeInboundOutbound(): void {
    const messages = [];
    if (this.party.notifications) {
     messages.push(...this.party.notifications);
    }
    if (this.party.inbounds) {
      messages.push(...this.party.inbounds);
    }

    this.filteredMessages = messages
      .filter(message => message.status_sent !== 'not_sent')
      .sort((a, b) => b.time - a.time);
  }

  partiesEventHandler(): void {
    this.partiesService.getById(this.party._id)
      .subscribe({
        next: (party: IParty) => {
          this.party = party;
          this.mergeInboundOutbound();
        },
        error: () => this.party = null,
      });
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  savedReplyOptionText = (item: Reply): string => {
    return `<p class="m-0 line-zero-seven">
      <span class="font-weight-bold">${item.name}</span>
      <br><span class="text-wrap">${item.text}</span>
      </p>`;
  };

  savedReplyText = (item: Reply): string => {
    return item.name;
  };

  sendSMS(): void {
    this.messageSenderService.smsPage(this.party as IParty, this.smsReplyControl.value.trim(), 'chat');
    this.form.reset();
  }

  onDropdownTriggered(opened: boolean): void {
    this.dropDownOpened = opened;
  }

  get smsReplyControl(): AbstractControl {
    return this.form.get('smsReply');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
